import React, { PropsWithChildren } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useCreateCalendar, useUpdateCalendar } from '@app/sdk';
import { toFormattedDate } from '@app/utilities';
import {
  CheckBox,
  CheckBoxProps,
  ContentHeader,
  FormContainer,
  Input,
  InputProps,
  Select,
  SelectProps,
  TextArea,
  TextAreaProps,
  toSelectOption,
} from '@app/shared';
import { CalendarFormModel } from './Calendars';

import { useRefreshCalendars } from '../../../../sdk/tms/referenceData/UseCalendars';

export function CalendarForm({
  calendar,
  children,
  selectedCalendarType,
}: PropsWithChildren<{
  calendar: CalendarFormModel;
  selectedCalendarType: string;
}>) {
  const formContext = useForm<CalendarFormModel>();
  const { mutateAsync: updateCalendar, isPending: isUpdatePending } = useUpdateCalendar();
  const { mutateAsync: createCalendar, isPending: isCreatePending } = useCreateCalendar();
  const refreshCalendar = useRefreshCalendars();

  async function handleSubmit(data: CalendarFormModel) {
    if (calendar?.id) {
      await updateCalendar({
        id: calendar.id,
        calendar: {
          ...calendar,
          ...data,
          type: selectedCalendarType,
          startDate: toFormattedDate(data.startDate),
          endDate: toFormattedDate(data.endDate),
          nonWorkingDays: data.nonWorkingDays ? data.nonWorkingDays : [],
          holidays: data.holidays
            .split('\n')
            .map((h) => toFormattedDate(h).trim())
            .filter((x) => x != ''),
        },
      });
    } else {
      await createCalendar({
        type: data.type,
        startDate: toFormattedDate(data.startDate),
        endDate: toFormattedDate(data.endDate),
        nonWorkingDays: data.nonWorkingDays,
        holidays: data.holidays
          .split('\n')
          .map((h) => toFormattedDate(h).trim())
          .filter((x) => x != ''),
      });
    }

    refreshCalendar();
  }

  const startDate: InputProps<CalendarFormModel> = {
    name: 'startDate',
    options: { required: 'Start date is required.' },
    label: 'Start Date',
    type: 'date',
    defaultValue: calendar?.startDate,
  };
  const endDate: InputProps<CalendarFormModel> = {
    name: 'endDate',
    options: { required: 'End date is required' },
    label: 'End Date',
    type: 'date',
    defaultValue: calendar?.endDate,
  };
  const nonWorkingDays: CheckBoxProps<CalendarFormModel> = {
    name: 'nonWorkingDays',
    checkboxOptions: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    label: 'Non Working Days',
    defaultValue: calendar?.nonWorkingDays,
  };
  const holidays: TextAreaProps<CalendarFormModel> = {
    name: 'holidays',
    label: 'Holidays',
    placeholder: 'yyyy-MM-dd separated by comma.',
    type: 'text',
    defaultValue: calendar?.holidays,
  };
  const type: SelectProps<CalendarFormModel> = {
    name: 'type',
    selectOptions: toSelectOption(['BACS', 'SEPA']),
    label: 'Type',
    defaultValue: selectedCalendarType,
    options: { required: 'Type is required.' },
  };

  const isSubmitting = () => isCreatePending || isUpdatePending;

  return (
    <FormProvider {...formContext}>
      <ContentHeader>{calendar ? 'Update Calendar' : 'Add Calendar'}</ContentHeader>
      <FormContainer onSubmit={handleSubmit} isSubmitting={isSubmitting()} disableButtons>
        {!calendar?.id && <Select {...type} disabled />}
        <Input {...startDate} disabled />
        <Input {...endDate} disabled />
        <CheckBox {...nonWorkingDays} disabled />
        <TextArea {...holidays} disabled />
        {children}
      </FormContainer>
    </FormProvider>
  );
}
